/**
 * lib/platformAuth.ts
 * Centralized handoff to skplatform-fe — the single auth UI for all Softknack products.
 * Cross-platform:
 *   • Web  — the original SSO flow (see PRODUCT-AUTH-INTEGRATION-GUIDE.md §4): write a
 *            one-time state nonce cookie, then window.location.assign to the platform.
 *   • Native — no browser cookie jar to share, so we open the platform login in the
 *            device browser via Linking. Cookie-based SSO does not carry into a native
 *            app; native session bootstrapping is handled platform-side (see auth notes).
 */
import { Platform, Linking } from 'react-native';

function deriveDefaultPlatformFeUrl(): string {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return 'https://platform.softknack.com';
  const host = window.location.hostname;
  if (host === 'localhost') return 'http://localhost:3009';
  if (host.endsWith('.dev.softknack.com')) return 'https://platform.dev.softknack.com';
  if (host.endsWith('.staging.softknack.com')) return 'https://platform.staging.softknack.com';
  return 'https://platform.softknack.com';
}

// VITE_PLATFORM_FE_URL is baked in at build time by webpack's DefinePlugin, which
// replaces this exact `process.env.VITE_PLATFORM_FE_URL` expression with a string
// literal — it does NOT define a `process` global. A `typeof process !== 'undefined'`
// guard here checks a DIFFERENT (never-replaced) reference, which is always
// 'undefined' in a browser bundle — that guard used to short-circuit this to
// `false` unconditionally, silently discarding the env var and always falling
// through to the hostname-derived default below (which, on localhost, points
// hosp at itself — an infinite self-redirect loop, not a login screen).
const PLATFORM_FE_URL: string = process.env.VITE_PLATFORM_FE_URL || deriveDefaultPlatformFeUrl();

function makeState(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function buildUrl(returnTo: string, state: string): string {
  const base = PLATFORM_FE_URL.endsWith('/') ? PLATFORM_FE_URL : `${PLATFORM_FE_URL}/`;
  const params = new URLSearchParams({ product: 'hosp', return_to: returnTo, state });
  return `${base}?${params.toString()}`;
}

export function handoffToPlatform(returnTo?: string): void {
  const state = makeState();

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const target = returnTo ?? window.location.href;
    const isLocalhost = window.location.hostname === 'localhost';
    const domainAttr = isLocalhost ? '' : '; Domain=.softknack.com; Secure';
    document.cookie = `sk_auth_state=${state}; Path=/; Max-Age=300; SameSite=Lax${domainAttr}`;
    window.location.assign(buildUrl(target, state));
    return;
  }

  // Native: open the platform login in the system browser (best-effort).
  void Linking.openURL(buildUrl(returnTo ?? '/', state));
}
